import { useCallback, useMemo, useState } from 'react';
import { AgGridReact } from "@ag-grid-community/react";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the Data Grid
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-theme-balham.css"
import '../components/TablaMaestra.css'
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import {ExcelExportModule, MenuModule, RowGroupingModule, SetFilterModule, FiltersToolPanelModule} from "ag-grid-enterprise";
import { fetchColumnDefinitions, fetchFilteredData } from '../services/claimsService';
import { reorderColumns } from "../utils/aggrid";
import { CargoClaimToolPanel } from "../components";
import {parseDatabaseDate} from "../utils/general";
import {Card, CardBody, Tab, Tabs} from "@heroui/react";
import ReclamosTable from "../components/aggrid/ReclamosTable";
import { differenceInDays } from 'date-fns';
import { useAuth } from "../components/auth/AuthProvider";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    MenuModule,
    RowGroupingModule,
    SetFilterModule,
    ExcelExportModule
]);

function Claims({ darkMode }) {
    //const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isChrome = true;
    const { userProfile } = useAuth();
    const permissions = userProfile?.permissions || [];
    const hasPrivileges = permissions.length > 0;
    const baseRskUser = !hasPrivileges; // Usuario autenticado por perfil 2/5/6/8 sin privilegios explícitos

    const isAdmin = permissions.includes("admin");
    const isJefeJudicial = permissions.includes("jefejudicial");
    const isRecovery = permissions.includes("recovery") || userProfile?.profile_id === 5;
    const isFinanceUser = permissions.includes("aprobadorfinanciero");
    const isClaimsRead = permissions.includes("claimsread");

    // Los usuarios base RSK (sin privilegios) pueden aprobar/crear
    const canApproveCreate = isAdmin || isRecovery || baseRskUser;
    const isFinanceAllowed = isFinanceUser || isAdmin; // admin también puede finance-approve

    const readOnlyClaims = isClaimsRead && !canApproveCreate && !isFinanceUser && !isAdmin && !isJefeJudicial;
    // Row Data: The data to be displayed.
    const [rowData, setRowData] = useState();

    // Column Definitions: Defines the columns to be displayed.
    const [colDefs, setColDefs] = useState([]);
    const [gridApi, setGridApi] = useState(null); //almacenar grid api para pasarlo al toolpanel

    const defaultColDef = useMemo(() => {
        return {
            filter: 'agTextColumnFilter',
            floatingFilter: true,
        };
    }, []);
    const forceSingleSelection = isFinanceUser && !canApproveCreate;
    const rowSelection = useMemo(() => (forceSingleSelection ? "single" : "multiple"), [forceSingleSelection]);

    const getRowId = useCallback((params) => params.data.id, []);

    const onCellEditRequest = useCallback(async (event) => {
        const oldData = event.data;
        const field = event.colDef.field; //Column
        const newValue = event.newValue; //Value
        const newData = { ...oldData };
        const dataToUpdate = { 'id': newData.id, 'column': field, 'newValue': newValue, 'oldValue': event.oldValue }
        newData[field] = event.newValue;
        console.log("onCellEditRequest, updating " + field + " to " + newValue);
        console.log("Data to update: ", dataToUpdate);
        console.log("event: ", event);

        //undefined deberia hacer un return

        try {
            //TODO: ver si realmente necesita actualizar datos en esta vista
            return;
            // SEND POST REQUEST
            const response = await fetch('https://quickview.rskgroupco.com/app/judicial/updateone', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToUpdate),
            });

            // HANDLE RESPONSE
            if (response.ok) {
                const updatedData = await response.json(); // Si el servidor devuelve datos
                console.log("Actualización exitosa", updatedData);

                // APPLY AGGRID TX
                const tx = {
                    update: [newData],
                };
                event.api.applyTransaction(tx);

            } else {
                const errorText = await response.text();
                console.error("Error en el servidor: ", errorText);
                alert("Error al actualizar el dato: " + errorText);
            }

        } catch (error) {
            console.error("Error al enviar la solicitud: ", error);
            alert("Error al conectar con el servidor.");
        }

    }, []);

    const getMoneySymbolByColumn = (columnName) => {
        let symbol;
        switch(columnName){
            case 'monto_indemnizado_usd':
            case 'reclamo_usd':
            case 'expectativas_usd':
            case 'recuperado_usd':
                symbol = '$';
                break;

            case 'reclamo_euros':
            case 'recuperado_euros':
                symbol = '€';
                break;

            default:
                symbol = '';
                break;
        }
        return symbol
    }

    const editableColumns = [];
    const hiddenColumns = [
        'fecha_actualizacion',
        'fecha_creacion'
    ];
    const rowGroupColumns = [
        'embarcador',
        'status',
        'contraparte',
        'nave'
    ];

    const setColumTypeFromPostgresTableDefinition = (column) => {
        let colData = {
            rowGroup: rowGroupColumns.includes(column.column_name),
            rowGroupIndex: rowGroupColumns.indexOf(column.column_name),
            hide: hiddenColumns.includes(column.column_name)
        };

        //console.log("type: " + column.column_name);
        //console.log("column data: ", column)

        //Filtrar por tipo de dato
        switch(column.data_type){
            case "uuid":
                if(column.column_name === "id"){
                    colData = {
                        field: column.column_name, editable: editableColumns.includes(column.column_name),
                        hide: true,
                        suppressColumnsToolPanel: true,
                        cellClassRules: {
                            'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                        }
                    };
                }else{
                    colData = {
                        field: column.column_name, editable: editableColumns.includes(column.column_name),
                        cellClassRules: {
                            'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                        },
                    };
                }

                break;
            case "timestamp without time zone":
            case "timestamp with time zone": //ambos timestamp
            case "date":
                colData =
                    {
                        ...colData,
                        field: column.column_name, cellEditor: 'agDateCellEditor', editable: editableColumns.includes(column.column_name),
                        filter: 'agDateColumnFilter',
                        valueGetter: (params) => {
                            //Convertir a fecha de JS
                            return params.data && params.data[column.column_name]
                                //? new Date(params.data[column.column_name])
                                ? parseDatabaseDate(params.data[column.column_name])
                                : null;
                        },
                        valueFormatter: (params) => {
                            // Formatea la fecha para mostrarla como texto en la celda
                            return params.value ? params.value.toLocaleDateString("es-ES", { timeZone: "UTC" }) : '';
                            //return params.value ? params.value.toLocaleDateString() : '';
                        },
                        cellClassRules: {
                            'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                        },
                        enableRowGroup: true,
                        enablePivot: true,
                    };

                break;
            case "character varying": //character_maximum_length: 255
                colData =   {
                    ...colData,
                    field: column.column_name, cellEditor: 'agTextCellEditor', editable: editableColumns.includes(column.column_name),
                    cellEditorParams: {
                        maxLength: column.character_maximum_length
                    },
                    sort: column.column_name === 'rsk_ref_number' ? 'desc' : false,
                    cellClassRules: {
                        'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                    },
                    enableRowGroup: true,
                    enablePivot: true
                }
                break;
            case "text": //no character limit
                colData = {
                    field: column.column_name, cellEditor: 'agLargeTextCellEditor', editable: editableColumns.includes(column.column_name),
                    cellEditorPopup: true,
                    cellClassRules: {
                        'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                    },
                    enableRowGroup: true,
                    enablePivot: true,
                };

                break;
            case "smallint": //numeric_precision: 16 (smallint = 16) / numeric_scale (normally numeric scale for smallint is 0)
            case "integer": //numeric_precision: 32 (int32) / numeric_scale both integers (normally numeric scale for integer is 0)
                colData = {
                    field: column.column_name, cellEditor: 'agNumberCellEditor', editable: editableColumns.includes(column.column_name),
                    cellEditorParams: {
                        min: -Math.pow(10, column.numeric_precision) + 1,
                        max: Math.pow(10, column.numeric_precision) - 1
                    },
                    cellClassRules: {
                        'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                    },
                    enableValue: true,
                }

                break;
            case "numeric": // numeric_precision / numeric_scale both integers
                // Cálculo del rango
                const max = Math.pow(10, column.numeric_precision - column.numeric_scale) - Math.pow(10, -column.numeric_scale); // Máximo positivo
                const min = -max; // Mínimo negativo
                colData = {
                    field: column.column_name,
                    cellEditor: 'agNumberCellEditor',
                    editable: editableColumns.includes(column.column_name),
                    cellEditorParams: {
                        min: min,
                        max: max,
                        precision: column.numeric_scale
                        // step: Math.pow(10, -column.numeric_scale), // Incremento basado en la escala
                    },
                    valueFormatter: (params) => {
                        // Verifica si hay un valor para formatear
                        if (params.value == null || isNaN(params.value)) return '';

                        // Aplica Intl.NumberFormat con es-CL
                        const numberFormatter = new Intl.NumberFormat("es-CL", {
                            minimumFractionDigits: column.numeric_scale,
                            maximumFractionDigits: column.numeric_scale,
                        });

                        // Incluye el símbolo de dinero si aplica
                        const symbol = getMoneySymbolByColumn(column.column_name);
                        return `${symbol} ${numberFormatter.format(Number(params.value))}`;
                    },
                    valueGetter: (params) => {
                        return params.data && params.data[column.column_name] ? Number(params.data[column.column_name]) : null;
                    },
                    cellClassRules: {
                        'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                    },
                    enableValue: true,
                };

                break;
            default:
                colData = {
                    field: column.column_name, editable: editableColumns.includes(column.column_name),
                    cellClassRules: {
                        'non-editable-cell': (params) => !params.colDef.editable, // Aplica la clase si no es editable
                    },
                };
                break;
        }

        return colData;
    }

    const onGridReady = useCallback(async (params) => {

        try {
            // Obtener definición de columnas
            const columnDefinitions = await fetchColumnDefinitions();
            const baseColDefs = columnDefinitions.map(setColumTypeFromPostgresTableDefinition);

            const priorityColumnsLocal = ["rsk_ref", "status", "embarcador", "contraparte", "nave", "bl_awb_date", "reclamo_usd"];
            const reorderedColDefsLocal = reorderColumns(baseColDefs, priorityColumnsLocal);

            const durationColumns = [
                {
                    headerName: "Days in Finance Check",
                    field: "days_in_finance_check",
                    filter: "agNumberColumnFilter",
                    sortable: true,
                    valueFormatter: (params) => {
                        if (params.node?.group) return "";
                        return params.value != null ? `${params.value} days` : "";
                    },
                },
                {
                    headerName: "Days in Ready to Claim",
                    field: "days_in_ready_to_claim",
                    filter: "agNumberColumnFilter",
                    sortable: true,
                    valueFormatter: (params) => {
                        if (params.node?.group) return "";
                        return params.value != null ? `${params.value} days` : "";
                    },
                },
            ];

            const statusIndex = reorderedColDefsLocal.findIndex(col => col.field === "status");
            if (statusIndex !== -1) {
                reorderedColDefsLocal.splice(statusIndex + 1, 0, ...durationColumns);
            } else {
                reorderedColDefsLocal.push(...durationColumns);
            }

            setColDefs(reorderedColDefsLocal);

            // Obtener datos y filtrar por estatus mostrados en Ready to Claim view
            const rawData = await fetchFilteredData();
            let filteredRows = (rawData || []).filter(
                (row) => row?.status === "Ready to Claim" || row?.status === "Finance Check"
            );

            setRowData(filteredRows);
        } catch (error) {
            console.error('Error al cargar los datos:', error);
        }

        setGridApi(params.api); //setear el gridapi para pasarlo al toolpanel
    }, [isFinanceUser]);

    //Sidebar config
    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: "columns",
                    labelDefault: "Columns",
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                },
                {
                    id: "filters",
                    labelDefault: "Filters",
                    labelKey: "filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel",
                },
                {
                    id: "cargoClaim",
                    labelDefault: "Cargo Claim",
                    labelKey: "cargoClaim",
                    iconKey: "cargo-claim",
                    toolPanel: CargoClaimToolPanel,
                    toolPanelParams: {
                        title: (
                            <span style={{ fontWeight: "normal", color: "black", fontSize: 20}}>
                                🚢 Cargo Claim
                            </span>
                        ),
                        readOnlyClaims,
                        canApproveCreate,
                        isFinanceUser,
                        isFinanceAllowed,
                        isJefeJudicial,
                    },
                },
            ],
            defaultToolPanel: "cargoClaim",
        };
    }, [gridApi, readOnlyClaims, canApproveCreate, isFinanceUser, isFinanceAllowed]);


    return (
        <div className="flex flex-col w-full">
            <Tabs aria-label="Options" size="sm" className="flex flex-col">
                <Tab key="ready_to_claim" title="Ready to Claim">
                    <div className={`${isChrome ? "" : "flex-1 flex flex-col"}`}>
                        <div className={`${darkMode ? "ag-theme-balham-dark" : "ag-theme-balham"} flex-1 w-full h-[calc(100vh-124px)]`}>
                            <AgGridReact
                                rowData={rowData}
                                columnDefs={colDefs}
                                defaultColDef={defaultColDef}
                                rowSelection={rowSelection}
                                getRowId={getRowId}
                                readOnlyEdit={true}
                                onCellEditRequest={onCellEditRequest}
                                sideBar={sideBar}
                                onGridReady={onGridReady}
                                rowGroupPanelShow={"always"}
                                autoGroupColumnDef={{
                                    minWidth: 150,
                                    width: 220,
                                    cellRendererParams: {
                                        suppressCount: false, // mostrar el contador de filas
                                    },
                                }}
                            />
                        </div>
                    </div>
                </Tab>
                {!(isFinanceUser || isClaimsRead) && (
                    <Tab key="claimed" title="Reclamados">
                        <ReclamosTable darkMode={darkMode} />
                    </Tab>
                )}
            </Tabs>
        </div>


    );
}

export default Claims;